import React, { useState, useEffect } from 'react';
import {
  Chip,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import SignalCellularConnectedNoInternet0BarIcon from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import {
  subscribeToSyncStatus,
  syncOfflineTransactions,
} from '../../services/offlineSyncService';
import { getPendingCount, getFailedCount } from '../../utils/offlineDB';
import { useLocationContext } from '../../context/LocationContext';

export default function ConnectivityIndicator({ sx = {}, size = 'small' }) {
  const locationCtx = useLocationContext();
  const [syncState, setSyncState] = useState({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSyncing: false,
    lastSyncTime: null,
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  const refreshCounts = async () => {
    try {
      const pCount = await getPendingCount();
      const fCount = await getFailedCount();
      setPendingCount(pCount);
      setFailedCount(fCount);
      if (locationCtx && locationCtx.setSyncQueueCount) {
        locationCtx.setSyncQueueCount(pCount);
      }
    } catch {
      // Ignore count fetch errors
    }
  };

  useEffect(() => {
    refreshCounts();

    const unsubscribe = subscribeToSyncStatus((status) => {
      setSyncState(status);
      refreshCounts();
    });

    const interval = setInterval(refreshCounts, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  const handleManualSync = async () => {
    if (!syncState.isOnline || syncState.isSyncing) return;
    await syncOfflineTransactions();
    await refreshCounts();
  };

  const isOnline = syncState.isOnline;
  const isSyncing = syncState.isSyncing;

  let label = 'Online';
  let color = 'success';
  let icon = <SignalCellularAltIcon fontSize="small" />;

  if (!isOnline) {
    label = pendingCount > 0 ? `Offline (${pendingCount})` : 'Offline';
    color = 'error';
    icon = <SignalCellularConnectedNoInternet0BarIcon fontSize="small" />;
  } else if (isSyncing) {
    label = pendingCount > 0 ? `Syncing (${pendingCount})` : 'Syncing';
    color = 'warning';
    icon = <CircularProgress size={12} color="inherit" thickness={6} />;
  } else if (pendingCount > 0) {
    label = `Online (${pendingCount})`;
    color = 'warning';
    icon = <CloudSyncIcon fontSize="small" />;
  }

  const tooltipTitle = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {isOnline ? 'Cloud POS Connected' : 'Offline Mode Active'}
      </Typography>
      <Typography variant="caption" display="block">
        Pending Sync Queue: <strong>{pendingCount} transactions</strong>
      </Typography>
      {failedCount > 0 && (
        <Typography variant="caption" display="block" color="error.light">
          Requires Attention: <strong>{failedCount} failed</strong>
        </Typography>
      )}
      {syncState.lastSyncTime && (
        <Typography variant="caption" display="block" color="text.secondary">
          Last Sync: {new Date(syncState.lastSyncTime).toLocaleTimeString()}
        </Typography>
      )}
      {isOnline && pendingCount > 0 && !isSyncing && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', color: 'primary.light' }}>
          Click to sync queue now
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipTitle} arrow placement="bottom">
      <Chip
        icon={icon}
        label={label}
        size={size}
        color={color}
        variant="outlined"
        onClick={isOnline && pendingCount > 0 && !isSyncing ? handleManualSync : undefined}
        sx={{
          fontWeight: 700,
          borderRadius: 1.5,
          cursor: isOnline && pendingCount > 0 ? 'pointer' : 'default',
          ...sx,
        }}
      />
    </Tooltip>
  );
}
